/**
 * @fileoverview Firebase matchmaking queue management
 * Handles adding, querying, and removing players from matchmaking queue
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebaseConfig';

const MATCHMAKING_COLLECTION = 'matchmaking';

export interface MatchmakingEntry {
  userId: string;
  gameId: string;
  rating: number;
  searchStartTime: number;
  preferredMode: 'competitive' | 'casual';
  timeControlPreference?: 'sprint' | 'standard' | 'marathon' | 'any';  // Time control preference
  isPlaced?: boolean;  // True if player has completed placement (5 games), false if still in placement
  timestamp?: Timestamp;
}

/**
 * Add a player to the matchmaking queue
 */
export async function addMatchmakingEntry(entry: MatchmakingEntry): Promise<void> {
  const matchmakingCollection = collection(db, MATCHMAKING_COLLECTION);


  // Remove any existing entry for this user first
  await removeMatchmakingEntry(entry.userId);

  // Add new entry
  const docRef = await addDoc(matchmakingCollection, {
    ...entry,
    timestamp: Timestamp.now()
  });

  // Verify it was added
  const verifySnapshot = await getDocs(matchmakingCollection);
}

/**
 * Query matchmaking queue for compatible opponents
 * @param excludeUserId - Don't match with self
 * @param userRating - Player's current ELO
 * @param eloRange - Maximum ELO difference allowed
 * @param preferredMode - Matchmaking mode preference
 * @param timeControlPreference - Time control preference
 * @param isPlaced - Whether the searching player has completed placement (affects cross-mode matching)
 */
export async function queryMatchmakingQueue(
  excludeUserId: string,
  userRating: number,
  eloRange: number,
  preferredMode: 'competitive' | 'casual',
  timeControlPreference?: 'sprint' | 'standard' | 'marathon' | 'any',
  isPlaced?: boolean
): Promise<MatchmakingEntry[]> {
  const matchmakingCollection = collection(db, MATCHMAKING_COLLECTION);


  try {
    // TEMPORARY: Get all matchmaking entries and filter client-side
    // This avoids needing a composite index during initial testing
    // TODO: Once index is created, optimize this query
    const querySnapshot = await getDocs(matchmakingCollection);

    const entries: MatchmakingEntry[] = [];
    const filtered: any[] = [];

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const filterReason: string[] = [];


      // Skip self
      if (data.userId === excludeUserId) {
        filterReason.push('IS SELF');
        filtered.push({ ...data, filterReason: filterReason.join(', ') });
        return;
      }

      // Check ELO range (if not infinity for casual)
      if (eloRange !== Infinity) {
        const ratingDiff = Math.abs(data.rating - userRating);
        if (ratingDiff > eloRange) {
          filterReason.push(`ELO DIFF ${ratingDiff} > ${eloRange}`);
          filtered.push({ ...data, filterReason: filterReason.join(', ') });
          return;
        }
      }

      // Filter by mode in client-side
      const opponentMode = data.preferredMode;
      const opponentIsPlaced = data.isPlaced ?? true; // Default to true (ranked) if not specified
      const myIsPlaced = isPlaced ?? true; // Default to true (ranked) if not specified

      // Mode matching logic:
      // - Casual matches with: casual OR placement players (competitive with isPlaced=false)
      // - Placement (competitive with isPlaced=false) matches with: competitive OR casual
      // - Ranked competitive (isPlaced=true) matches with: competitive only
      // This allows placement players to get games more easily from the casual pool
      let isCompatibleMode = false;

      if (preferredMode === 'casual') {
        // Casual can match with: casual, OR placement players only (not ranked competitive)
        isCompatibleMode = opponentMode === 'casual' ||
          (opponentMode === 'competitive' && !opponentIsPlaced);
      } else if (preferredMode === 'competitive') {
        if (!myIsPlaced) {
          // Placement player can match with: competitive (any) OR casual
          isCompatibleMode = opponentMode === 'competitive' || opponentMode === 'casual';
        } else {
          // Ranked competitive can only match with competitive
          isCompatibleMode = opponentMode === 'competitive';
        }
      }


      if (!isCompatibleMode) {
        filterReason.push(`MODE INCOMPATIBLE (${preferredMode} vs ${opponentMode})`);
        filtered.push({ ...data, filterReason: filterReason.join(', ') });
        return;
      }

      // Time control matching logic (if specified)
      if (timeControlPreference) {
        const opponentTimeControl = data.timeControlPreference || 'standard';
        const isCompatibleTimeControl =
          timeControlPreference === 'any' ||           // We accept any time control
          opponentTimeControl === 'any' ||            // Opponent accepts any time control
          timeControlPreference === opponentTimeControl; // Exact match


        if (!isCompatibleTimeControl) {
          filterReason.push(`TIME CONTROL INCOMPATIBLE (${timeControlPreference} vs ${opponentTimeControl})`);
          filtered.push({ ...data, filterReason: filterReason.join(', ') });
          return;
        }
      }

      entries.push({
        userId: data.userId,
        gameId: data.gameId,
        rating: data.rating,
        searchStartTime: data.searchStartTime,
        preferredMode: data.preferredMode,
        timeControlPreference: data.timeControlPreference,
        isPlaced: data.isPlaced,
        timestamp: data.timestamp
      });
    });

    if (filtered.length > 0) {
    }

    return entries;
  } catch (error) {
    return [];
  }
}

/**
 * Remove a player from the matchmaking queue
 */
export async function removeMatchmakingEntry(userId: string): Promise<void> {
  const matchmakingCollection = collection(db, MATCHMAKING_COLLECTION);
  const q = query(matchmakingCollection, where('userId', '==', userId));


  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.size > 0) {
      querySnapshot.docs.forEach(doc => {
      });
    }

    const deletePromises = querySnapshot.docs.map((docSnapshot) =>
      deleteDoc(doc(db, MATCHMAKING_COLLECTION, docSnapshot.id))
    );
    await Promise.all(deletePromises);

    // Verify removal
    const verifySnapshot = await getDocs(matchmakingCollection);
  } catch (error) {
    throw error;
  }
}

/**
 * Get total number of players in matchmaking queue
 */
export async function getQueueSize(): Promise<number> {
  const matchmakingCollection = collection(db, MATCHMAKING_COLLECTION);
  const querySnapshot = await getDocs(matchmakingCollection);
  return querySnapshot.size;
}

/**
 * Clean up stale matchmaking entries (older than 5 minutes)
 * Should be called periodically or via Cloud Function
 */
export async function cleanupStaleEntries(): Promise<void> {
  const matchmakingCollection = collection(db, MATCHMAKING_COLLECTION);
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

  const q = query(
    matchmakingCollection,
    where('searchStartTime', '<', fiveMinutesAgo)
  );

  try {
    const querySnapshot = await getDocs(q);
    const deletePromises = querySnapshot.docs.map((docSnapshot) =>
      deleteDoc(doc(db, MATCHMAKING_COLLECTION, docSnapshot.id))
    );
    await Promise.all(deletePromises);
  } catch (error) {
  }
}
